// SellerMessages.js
import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Spinner } from "react-bootstrap";
import {
  FiSearch,
  FiUser,
  FiSend,
  FiExternalLink,
  FiPaperclip,
  FiX,
  FiArrowLeft,
} from "react-icons/fi";
import { useTheme } from "../../../../contexts/ThemeContext";
import SellerMasterLayout from "../../../../layouts/seller/SellerMasterLayout";
import AppContext from "../../../../contexts/AppContext";
import "../../../../assets/css/seller/screens/SellerMessages.css";

const SellerMessages = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { baseUrl, baseUrlForFiles,sellerAuthToken } = useContext(AppContext);

  const productIdToOpen = location.state?.productId;

  const [messagesList, setMessagesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [chatFile, setChatFile] = useState(null);
  const [chatFilePreview, setChatFilePreview] = useState(null);

  const toUtcDate = (dateString) => {
    if (!dateString) return null;
    return new Date(
      dateString.includes("T") ? dateString : dateString + " UTC",
    );
  };

  const isUnread = (item) => {
    if (!item.latest_message || item.latest_message.sender !== "chcustomer") {
      return false;
    }
    if (!item.seller_read_at) {
      return true;
    }
    return (
      toUtcDate(item.latest_message.created_at) > toUtcDate(item.seller_read_at)
    );
  };
  const viewChatMessages = (messageId) => {
    axios
      .get(`${baseUrl}/seller/conversation/getMessages`, {
        params: { conversation_id: messageId },
        headers: {
          Authorization: `Bearer ${sellerAuthToken}`,
        },
      })
      .then((response) => {
        setChatMessages(response.data.messages);
      })
      .catch(() => {});
  };

  const markConversationRead = (messageId) => {
    setMessagesList((prevList) =>
      prevList.map((item) =>
        item.id === messageId
          ? { ...item, seller_read_at: new Date().toISOString() }
          : item,
      ),
    );

    const formdata = new FormData();
    formdata.append("conversation_id", messageId);

    axios
      .post(`${baseUrl}/seller/conversation/markAsRead`, formdata,
      {
        headers: {
          "Content-Type": "multipart/form-data",
           Authorization: `Bearer ${sellerAuthToken}`,
        },
      },
)
      .catch(() => {});
  };

  const selectMessage = (item) => {
    setSelectedMessage(item);
    viewChatMessages(item.id);
    if (isUnread(item)) {
      markConversationRead(item.id);
    }
  };

  const closeChat = () => {
    setSelectedMessage(null);
  };

  const viewAllMessages = () => {
    setLoading(true);
    const loggedInSeller = JSON.parse(localStorage.getItem("loggedInSeller"));
    const sellerId = loggedInSeller?.id;

    axios
      .post(
        `${baseUrl}/seller/conversation/getAll`,
        { seller_id: sellerId },
        {
          headers: {
            Authorization: `Bearer ${sellerAuthToken}`,
          },
        },
      )
      .then((response) => {
        setMessagesList(response.data.conversations);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const loadInitialMessages = () => {
    setLoading(true);
    const loggedInSeller = JSON.parse(localStorage.getItem("loggedInSeller"));
    const sellerId = loggedInSeller?.id;

    axios
      .post(
        `${baseUrl}/seller/conversation/getAll`,
        { seller_id: sellerId },
        {
          headers: {
            Authorization: `Bearer ${sellerAuthToken}`,
          },
        },
      )
      .then((response) => {
        const conversations = response.data.conversations;
        setMessagesList(conversations);
        setLoading(false);

        if (productIdToOpen) {
          const match = conversations.find(
            (item) => item.product?.id === productIdToOpen,
          );
          if (match) {
            selectMessage(match);
          }
        }
      })
      .catch(() => setLoading(false));
  };

  const handleChatFileChange = (e) => {
    const file = e.target.files[0] || null;
    setChatFile(file);
    setChatFilePreview(file ? URL.createObjectURL(file) : null);
  };

  const removeChatFile = () => {
    setChatFile(null);
    setChatFilePreview(null);
  };

  const sendChatMessage = () => {
    if (!newMessage.trim() && !chatFile) return;

    const formdata = new FormData();
    formdata.append("conversation_id", selectedMessage.id);
    formdata.append("message", newMessage);
    if (chatFile) {
      formdata.append("file_path", chatFile);
    }
    setNewMessage("");
    setChatFile(null);
    setChatFilePreview(null);

    axios
      .post(`${baseUrl}/seller/conversation/sendMessage`,formdata,
      {
        headers: {
          "Content-Type": "multipart/form-data",
           Authorization: `Bearer ${sellerAuthToken}`,
        },
      },
)
      .then(() => {
        viewChatMessages(selectedMessage.id);
      })
      .catch(() => {});
  };

  const acceptCustomization = () => {
    const formdata = new FormData();
    formdata.append("conversation_id", selectedMessage.id);

    axios
      .post(`${baseUrl}/seller/conversation/confirm`, formdata,
      {
        headers: {
          "Content-Type": "multipart/form-data",
           Authorization: `Bearer ${sellerAuthToken}`,
        },
      },
)
      .then(() => {
        setSelectedMessage({ ...selectedMessage, status: "confirmed" });
        viewAllMessages();
      })
      .catch(() => {});
  };

  const declineCustomization = () => {
    const formdata = new FormData();
    formdata.append("conversation_id", selectedMessage.id);

    axios
      .post(`${baseUrl}/seller/conversation/decline`, formdata,
      {
        headers: {
          "Content-Type": "multipart/form-data",
           Authorization: `Bearer ${sellerAuthToken}`,
        },
      },
)
      .then(() => {
        setSelectedMessage({ ...selectedMessage, status: "declined" });
        viewAllMessages();
      })
      .catch(() => {});
  };

  useEffect(() => {
    loadInitialMessages();
  }, []);

  const getTimeAgo = (dateString) => {
    const minutes = Math.floor((new Date() - new Date(dateString)) / 60000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return "Yesterday";
    return `${days}d ago`;
  };

  const filteredMessagesList = messagesList.filter((item) => {
    if (!item.latest_message) return false;

    const customerName = `${item.customer?.first_name || ""} ${item.customer?.last_name || ""}`;
    const productName = item.product?.product_name || "";
    const matchesSearch =
      customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      productName.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesSearch;
  });

  return (
    <SellerMasterLayout>
      <div className={`seller-messages-page ${isDark ? "dark-mode" : ""}`}>
        <div className="seller-messages-card">
          <div className="seller-messages-box">
            {/* LEFT — list of all messages */}
            <div
              className={`seller-messages-sidebar ${selectedMessage ? "seller-messages-sidebar--mobile-hidden" : ""}`}
            >
              <h3 className="seller-messages-sidebar-title">Messages</h3>

              <div className="seller-messages-search">
                <FiSearch size={14} />
                <input
                  placeholder="Search customer or product"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="seller-messages-list">
                {loading && (
                  <div className="text-center py-4">
                    <Spinner
                      animation="border"
                      size="sm"
                      style={{ color: "#ff8559" }}
                    />
                  </div>
                )}

                {!loading && filteredMessagesList.length === 0 && (
                  <p className="seller-messages-empty-text">
                    No conversations found.
                  </p>
                )}

                {!loading &&
                  filteredMessagesList.map((item) => (
                    <div
                      key={item.id}
                      className={`seller-messages-list-item ${selectedMessage?.id === item.id ? "active" : ""}`}
                      onClick={() => selectMessage(item)}
                    >
                      <div className="seller-messages-avatar-wrap">
                        <div className="seller-messages-avatar">
                          {item.customer?.first_name
                            ?.charAt(0)
                            .toUpperCase() || <FiUser size={14} />}
                        </div>
                        {isUnread(item) && (
                          <span className="seller-messages-unread-dot" />
                        )}
                      </div>
                      <div className="seller-messages-list-item-body">
                        <div className="seller-messages-list-item-top">
                          <p className="seller-messages-customer-name">
                            {item.customer?.first_name}{" "}
                            {item.customer?.last_name}
                          </p>
                          <span className="seller-messages-time">
                            {item.latest_message
                              ? getTimeAgo(item.latest_message.created_at)
                              : ""}
                          </span>
                        </div>
                        <div className="seller-messages-product-row">
                          {item.product?.images?.[0] && (
                            <img
                              src={`${baseUrlForFiles}/${item.product.images[0].image_path}`}
                              alt=""
                            />
                          )}
                          <span>{item.product?.product_name}</span>
                        </div>
                        <p className="seller-messages-preview-text">
                          {item.latest_message
                            ? item.latest_message.sender === "chseller"
                              ? `You: ${item.latest_message.type === "file" ? "Sent an image" : item.latest_message.message}`
                              : item.latest_message.type === "file"
                                ? "Sent an image"
                                : item.latest_message.message
                            : "No messages yet."}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* RIGHT — chat panel */}
            <div
              className={`seller-messages-chat-panel ${selectedMessage ? "seller-messages-chat-panel--mobile-visible" : ""}`}
            >
              {!selectedMessage && (
                <p className="seller-messages-select-hint">
                  Select a conversation to view messages.
                </p>
              )}

              {selectedMessage && (
                <>
                  <div className="seller-messages-chat-header">
                    <div className="seller-messages-chat-header-left">
                      <button
                        type="button"
                        className="seller-messages-mobile-back"
                        onClick={closeChat}
                        aria-label="Back to messages"
                      >
                        <FiArrowLeft size={18} />
                      </button>
                      <div className="seller-messages-avatar">
                        {selectedMessage.customer?.first_name
                          ?.charAt(0)
                          .toUpperCase() || <FiUser size={14} />}
                      </div>
                      <div>
                        <h4 className="seller-messages-chat-header-name">
                          {selectedMessage.customer?.first_name}{" "}
                          {selectedMessage.customer?.last_name}
                        </h4>
                      </div>
                    </div>

                    {selectedMessage.product && (
                      <div className="seller-messages-header-right">
                        {selectedMessage.status !== "confirmed" &&
                          selectedMessage.status !== "declined" && (
                            <div className="seller-messages-mini-actions">
                              <button
                                className="seller-messages-mini-confirm"
                                onClick={acceptCustomization}
                              >
                                Confirm
                              </button>
                              <button
                                className="seller-messages-mini-decline"
                                onClick={declineCustomization}
                              >
                                Decline
                              </button>
                            </div>
                          )}

                        <div
                          className="seller-messages-product-card"
                          onClick={() => navigate("/seller/products")}
                        >
                          {selectedMessage.product.images?.[0] && (
                            <img
                              src={`${baseUrlForFiles}/${selectedMessage.product.images[0].image_path}`}
                              alt=""
                            />
                          )}
                          <div>
                            <p className="seller-messages-product-card-name">
                              {selectedMessage.product.product_name}
                            </p>
                            <p className="seller-messages-product-card-price">
                              Rs {selectedMessage.product.price}
                            </p>
                          </div>
                          <FiExternalLink size={14} />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="seller-messages-chat-messages">
                    <p className="seller-messages-chat-started-text">
                      Discussion started for{" "}
                      {selectedMessage.product?.product_name}
                    </p>
                    {chatMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`seller-messages-message-row ${msg.sender === "chseller" ? "seller" : "customer"}`}
                      >
                        <div
                          className={`seller-messages-message-bubble ${msg.type === "file" ? "seller-messages-message-bubble--file" : ""}`}
                        >
                          {msg.type === "file" && msg.file_path && (
                            <img
                              src={`${baseUrlForFiles}/${msg.file_path}`}
                              alt="attachment"
                              className="seller-messages-message-img"
                            />
                          )}
                          {msg.message && (
                            <p className="seller-messages-message-text">
                              {msg.message}
                            </p>
                          )}
                          <span className="seller-messages-message-time">
                            {new Date(msg.created_at).toLocaleTimeString(
                              "en-US",
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {selectedMessage.status === "confirmed" && (
                    <div className="seller-messages-status-banner confirmed">
                      Customization confirmed
                    </div>
                  )}

                  {selectedMessage.status === "declined" && (
                    <div className="seller-messages-status-banner declined">
                      Customization declined
                    </div>
                  )}

                  {chatFilePreview && (
                    <div className="seller-messages-file-preview-row">
                      <img
                        src={chatFilePreview}
                        alt="preview"
                        className="seller-messages-file-preview-img"
                      />
                      <span className="seller-messages-file-preview-name">
                        {chatFile?.name}
                      </span>
                      <button
                        type="button"
                        className="seller-messages-file-preview-remove"
                        onClick={removeChatFile}
                      >
                        <FiX size={14} />
                      </button>
                    </div>
                  )}

                  <div className="seller-messages-input-row">
                    <label
                      htmlFor="seller-chat-file"
                      className="seller-messages-attach-button"
                    >
                      <FiPaperclip size={16} />
                    </label>
                    <input
                      id="seller-chat-file"
                      type="file"
                      accept="image/*"
                      onChange={handleChatFileChange}
                      className="seller-messages-file-input"
                    />
                    <input
                      type="text"
                      className="seller-messages-input"
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") sendChatMessage();
                      }}
                    />
                    <button
                      className="seller-messages-send-button"
                      onClick={sendChatMessage}
                    >
                      <FiSend />
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </SellerMasterLayout>
  );
};

export default SellerMessages;